import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { errorMessage, platformApi } from './platformApi';
import ModulePicker, { IModuleNode } from './ModulePicker';

interface IPlanFormProps {
  // edit this plan; omit to create a new one
  plan?: any;
  // plans a new plan can start from
  basePlans?: any[];
  // create a private plan for this account
  forAccount?: { _id: string; name: string };
  product?: 'erp' | 'pos';
  onClose: () => void;
  onSaved: (plan: any) => void;
}

const ERP_LIMITS = [
  { key: 'users', label: 'Users' },
  { key: 'locations', label: 'Inventory locations' },
  { key: 'companies', label: 'Companies per owner (GSTINs)' },
  { key: 'invoicesPerMonth', label: 'Invoices per month' },
  { key: 'items', label: 'Items' },
  { key: 'employees', label: 'Employees' },
  { key: 'storageMB', label: 'File storage (MB)' },
  { key: 'ocrPerMonth', label: 'OCR scans per month' },
];
const POS_LIMITS = [{ key: 'outlets', label: 'Outlets included' }];

const str = (v: any) => (v === undefined || v === null ? '' : String(v));

const formFrom = (plan: any) => ({
  name: plan?.name || '',
  status: plan?.status || 'active',
  isPublic: plan ? plan.isPublic !== false : false,
  trialDays: str(plan?.trialDays),
  monthly: str(plan?.pricing?.monthly),
  yearly: str(plan?.pricing?.yearly),
  userMonthly: str(plan?.seatPricing?.userMonthly),
  userYearly: str(plan?.seatPricing?.userYearly),
  locationMonthly: str(plan?.seatPricing?.locationMonthly),
  locationYearly: str(plan?.seatPricing?.locationYearly),
  limits: Object.fromEntries([...ERP_LIMITS, ...POS_LIMITS].map(({ key }) => [key, str(plan?.limits?.[key])])),
  modules: (plan?.product === 'pos' ? plan?.grantsErpModules : plan?.modules) || [],
});

const PlanFormDialog: FC<IPlanFormProps> = ({ plan, basePlans = [], forAccount, product, onClose, onSaved }) => {
  const editing = !!plan;
  const choices = useMemo(() => basePlans.filter((p) => !product || p.product === product), [basePlans, product]);
  const [basedOn, setBasedOn] = useState(editing ? '' : (choices.find((p) => p.code === 'enterprise') || choices[0])?.code || '');
  const base = editing ? plan : choices.find((p) => p.code === basedOn);
  const isPos = base?.product === 'pos';
  const [form, setForm] = useState(() => formFrom(base));
  const [catalog, setCatalog] = useState<{ modules: IModuleNode[]; features: { key: string; name: string }[] }>();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    platformApi.get('/plans/modules').then((response) => setCatalog(response.data)).catch(() => undefined);
  }, []);

  // A new plan starts as a copy of the chosen base plan
  const pickBase = (code: string) => {
    setBasedOn(code);
    const next = formFrom(choices.find((p) => p.code === code));
    setForm({ ...next, name: form.name, isPublic: false });
  };

  const set = (key: string) => (e: any) => setForm({ ...form, [key]: e.target.value });
  const setLimit = (key: string) => (e: any) => setForm({ ...form, limits: { ...form.limits, [key]: e.target.value } });

  const save = async () => {
    setError('');
    setSaving(true);
    const body: any = {
      name: form.name,
      status: form.status,
      isPublic: form.isPublic,
      trialDays: form.trialDays,
      pricing: { monthly: isPos ? '' : form.monthly, yearly: form.yearly },
      limits: form.limits,
      modules: form.modules,
      ...(isPos ? {} : { seatPricing: { userMonthly: form.userMonthly, userYearly: form.userYearly, locationMonthly: form.locationMonthly, locationYearly: form.locationYearly } }),
    };
    try {
      const response = editing
        ? await platformApi.put(`/plans/${plan._id}`, body)
        : await platformApi.post('/plans', { ...body, basedOn, forAccount: forAccount?._id });
      onClose();
      onSaved(response.data.plan);
    } catch (err: any) {
      setError(errorMessage(err));
      setSaving(false);
    }
  };

  const title = editing ? `Edit plan · ${plan.name}` : forAccount ? `Custom plan for ${forAccount.name}` : 'Add plan';

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
        {editing && plan.subscriptions > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {plan.subscriptions} account(s) are on this plan. Limit and module changes apply to them immediately; their price is fixed until renewal.
          </Alert>
        )}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e9ecf1' }}>
          <Tab label='Price & limits' sx={{ textTransform: 'none' }} />
          <Tab label={isPos ? 'POS modules' : 'Modules'} sx={{ textTransform: 'none' }} />
        </Tabs>

        {tab === 0 && (
          <Grid container spacing={2}>
            {!editing && (
              <Grid item xs={12} md={6}>
                <TextField select label='Start from' size='small' fullWidth value={basedOn} onChange={(e) => pickBase(e.target.value)} helperText='Copies its price, limits and modules'>
                  {choices.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField label='Plan name' size='small' fullWidth required value={form.name} onChange={set('name')} helperText={forAccount ? 'e.g. "Acme – Enterprise 2026"' : ' '} />
            </Grid>
            {!isPos && (
              <Grid item xs={6} md={3}>
                <TextField label='Monthly price (₹)' type='number' size='small' fullWidth value={form.monthly} onChange={set('monthly')} />
              </Grid>
            )}
            <Grid item xs={6} md={3}>
              <TextField label={isPos ? 'Yearly per outlet (₹)' : 'Yearly price (₹)'} type='number' size='small' fullWidth value={form.yearly} onChange={set('yearly')} />
            </Grid>
            <Grid item xs={12}>
              <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 13, margin: 0 }}>
                Prices are + GST. Leave both prices empty for a contract price ("Contact sales"). For limits, -1 = unlimited.
              </h5>
            </Grid>
            {(isPos ? POS_LIMITS : ERP_LIMITS).map(({ key, label }) => (
              <Grid item xs={6} md={3} key={key}>
                <TextField label={label} type='number' size='small' fullWidth value={form.limits[key]} onChange={setLimit(key)} />
              </Grid>
            ))}
            <Grid item xs={6} md={3}>
              <TextField label='Trial days' type='number' size='small' fullWidth value={form.trialDays} onChange={set('trialDays')} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select label='Status' size='small' fullWidth value={form.status} onChange={set('status')} helperText='Inactive plans cannot be activated'>
                <MenuItem value='active'>Active</MenuItem>
                <MenuItem value='inactive'>Inactive</MenuItem>
              </TextField>
            </Grid>
            {!forAccount && !plan?.forAccount && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Checkbox size='small' checked={form.isPublic} onChange={(e) => setForm({ ...form, isPublic: e.target.checked })} />}
                  label='Show to customers on the plans screen'
                />
              </Grid>
            )}
            {!isPos && (
              <>
                <Grid item xs={12}>
                  <h5 style={{ fontWeight: 600, color: '#374151', fontSize: 13, margin: '4px 0 0' }}>Extra seats</h5>
                  <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 12, margin: 0 }}>
                    Price of one extra user or location for the account's plan period. Seats stay valid until that period ends. Leave empty if not sold on this plan.
                  </h5>
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField label='Extra user – monthly (₹)' type='number' size='small' fullWidth value={form.userMonthly} onChange={set('userMonthly')} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField label='Extra user – yearly (₹)' type='number' size='small' fullWidth value={form.userYearly} onChange={set('userYearly')} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField label='Extra location – monthly (₹)' type='number' size='small' fullWidth value={form.locationMonthly} onChange={set('locationMonthly')} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField label='Extra location – yearly (₹)' type='number' size='small' fullWidth value={form.locationYearly} onChange={set('locationYearly')} />
                </Grid>
              </>
            )}
          </Grid>
        )}

        {tab === 1 && (
          catalog
            ? (
              <ModulePicker
                modules={catalog.modules.filter((m) => (isPos ? m.key === 'pos' : m.key !== 'pos'))}
                features={isPos ? [] : catalog.features}
                value={form.modules}
                onChange={(modules) => setForm({ ...form, modules })}
              />
            )
            : <h5 style={{ fontWeight: 'normal', color: '#6b7280' }}>Loading modules…</h5>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size='small' variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <LoadingButton size='small' variant='contained' loading={saving} disabled={!form.name.trim() || (!editing && !basedOn)} onClick={save} sx={{ textTransform: 'none' }}>
          {editing ? 'Save changes' : 'Create plan'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PlanFormDialog;
